import { RouteComponentProps } from "@reach/router"
import FormAutomation from "components/form-automation"
import DropdownInput from "components/inputs/dropdown"
import NumberInput from "components/inputs/number"
import TextInput from "components/inputs/text"
import Modal from "components/modal/basic-modal"
import ConfirmationModal from "components/modal/confirmation-modal"
import Popover from "components/popover/popover"
import { PopoverButtons_s } from "components/popover/popover-styles"
import LeaseTypes from "components/property/settings/lease-types"
import { DateText_s, Icon_s, PropertySettings_s } from "components/property/settings/property-settings-styles"
import { Toolbar } from "components/subnav"
import { field_attrs } from "components/table/lib/column-field"
import { updateValidation } from "components/table/table-automation"
import PropertyUnderwriting from "components/wrapper/header/property-underwriting"
import assetService, { Asset } from "data/asset-list"
import chartService, { Chart } from "data/chart"
import { DateDetailOptions } from "data/config"
import stateProvinceOptions from "data/config/state-province-options"
import folderService, { Folder } from "data/folder"
import inflationService from "data/inflation"
import { formatDate } from 'data/lib/dates'
import { local } from 'data/lib/local-json'
import lineItemsService from "data/line-items"
import notificationService from "data/notification"
import propertyService, { Property } from "data/property"
import underwritingService from "data/underwriting"
import _ from "lodash"
import moment from "moment"
import React, { FC, useEffect, useState } from "react"
import { Button_s } from "styles/app-styles"




const PropertySettings: FC<RouteComponentProps> = (props) => {
  const [property, setProperty] = useState<Property|null>(null)
  const [folder, setFolder] = useState<Folder|null>(null)
  const [isAMFolder, setIsAMFolder] = useState<boolean>(false)
  const [assets, setAssets] = useState<Asset[]>([])
  const [currentAsset, setCurrentAsset] = useState<Asset>({assetid: 0})
  const [currentAssetId, setCurrentAssetId] = useState<number>(0)
  const [assetOptions, setAssetOptions] = useState<[]>([])
  const [inflationOptions, setInflationOptions] = useState<[]>([])
  const [linkMode, setLinkMode] = useState<boolean>(false)
  const [charts, setCharts] = useState<Chart[]>([])
  const [currentChart, setCurrentChart] = useState<{}>({})
  const [showLeaseTypesModal, toggleLeaseTypesModal] = useState<boolean>(false)
  const [showUnlinkConfirmationModal, setShowUnlinkConfirmationModal] = useState<boolean>(false)
  const [showUnderwritingPopover, setShowUnderwritingPopover] = useState<boolean>(false)
  const [uwValScenarioOptions, setUWValScenarioOptions] = useState<Array<any>>([])
  const [uwValScenario, setUWValScenario] = useState<number>(0)
  const [uwBeginDate, setUWBeginDate] = useState<string>("")
  const [uwTerm, setUWTerm] = useState<number>(0)
  const [notes, setNotes] = useState<string>(_.get(property, "notedetail.notes") || '')

  useEffect(() => {
    const propertySubscription = propertyService.stream().subscribe(data => {
      if (data.value.length > 0 && parseInt(local("property")) !== _.get(property, "propertyid")) {
        let current = propertyService.get()
        const defaultValuation = underwritingService.getDefaultScenario()
        if(current.folderid !== _.get(folder, "folderid")) folderService.fetchFolder(current.folderid)
        if(!uwValScenarioOptions.length) setUWValScenarioOptions(_.map(_.get(current, "valuationdetail"), (v) => { return {label: v.valuationDescription, value: v.valuationPeriod} }))
        if(!uwValScenario) setUWValScenario(_.get(defaultValuation, "valuationPeriod"))
        if(!uwBeginDate) setUWBeginDate(underwritingService.getReportBeginDefault(true))
        if(!uwTerm) setUWTerm(underwritingService.getReportTermDefault(true))
        setProperty(current)
        setNotes(_.get(current, "notedetail.notes"))
        inflationService.fetchInflation(current.propertyid)
      }
    })

    const folderSubscription = folderService.stream().subscribe(data => {
      if (data.value.length > 0){
        let current = folderService.get()
        if(current.folderid !== _.get(folder, "folderid")) {
          folderService.isAssetManagementSubfolder(current.folderid)
          .then((res) => {
            setIsAMFolder(res)
          })
        }
        setFolder(current)
      }
    })

    const assetSubscription = assetService.stream().subscribe(data => {
      if (data.value.length > 0){
        setAssets(data.value)
      }
    })

    const chartSubscription = chartService.stream().subscribe(data => {
      if (data.value.length > 0){
        setCharts(data.value)
      }
    })

    const inflationSubscription = inflationService.stream().subscribe(data => {
      if (data.value.length > 0){
        setInflationOptions(_.map(data.value, (i) => { return { label: i.code , value: i.inflationid } }))
      }
    })

    assetService.fetchAssets()
    chartService.fetchCharts()

    return function cleanup() {
      propertySubscription.unsubscribe()
      folderSubscription.unsubscribe()
      assetSubscription.unsubscribe()
      chartSubscription.unsubscribe()
      inflationSubscription.unsubscribe()
    }
  }, [folder?.folderid, property?.propertyid])

  useEffect(() => {
    updateValidation()
  },[property])

  useEffect(() => {
    if(!property && !_.get(property, "assetid") && !folder && !_.get(folder, "chartid") && !assets.length && !charts.length) return

    let assetOptions = _.map(assets, (a) => { return {label: `${a.code} - ${a.name}`, value: a.assetid} })
    if(isAMFolder) assetOptions = _.map(_.filter(assets, (a) => a.chartid === _.get(folder, "chartid")), (a) => { return {label: `${a.code} - ${a.name}`, value: a.assetid} })
    setAssetOptions(assetOptions)

    const currentAsset = _.find(assets, (a) => a.assetid === _.get(property, "assetid"))
    setCurrentAsset(currentAsset)
    setCurrentAssetId(_.get(currentAsset, "assetid"))

    if(currentAsset) {
      const currentChart = _.find(charts, (c) => c.chartid === currentAsset.chartid)
      setCurrentChart(currentChart)
    }
  }, [property, folder, assets, charts])

  const persist = (field, val) => {
    if(field === "assetid") setLinkMode(false)
    const nextProperty = _.cloneDeep(property)

    if(field === "defaultshospitality.adrInflation") {
      _.set(nextProperty, field, {
        inflationMode: "namedItem",
        inflationNamedItemId: val
      })
    } else {
      _.set(nextProperty, field, val)
    }

    if(field === "analysisbegindate" && moment(val).isAfter(_.get(nextProperty, "reportbegindate"))) {
      _.set(nextProperty, "reportbegindate", val)
      notificationService.notify({
        type: "info",
        message: "Report Begin date adjusted to the Analysis Begin date",
        dismiss: 3000
      })
    } else if(field === "reportbegindate" && moment(val).isBefore(_.get(nextProperty, "analysisbegindate"))) {
      _.set(nextProperty, "reportbegindate", _.get(nextProperty, "analysisbegindate"))
      notificationService.notify({
        type: "info",
        message: "Report Begin date adjusted to the Analysis Begin date",
        dismiss: 3000
      })
    }

    setProperty(nextProperty)
    propertyService.patch(nextProperty)
  }

  const unlinkAsset = () => {
    setCurrentChart({code: ""})
    const nextProperty = _.cloneDeep(property)
    _.set(nextProperty, "assetid", null)
    _.set(nextProperty, "address1", currentAsset.address)
    _.set(nextProperty, "address2", null)
    _.set(nextProperty, "city", currentAsset.city)
    _.set(nextProperty, "state", currentAsset.state)
    _.set(nextProperty, "postalcode", currentAsset.postalcode)
    lineItemsService.clearDetailAccounts(nextProperty.propertyid)
    propertyService.patch(nextProperty)
    setShowUnlinkConfirmationModal(false)
  }

  const updateValuationScenario = (scenarioid: number) => {
    setUWValScenario(scenarioid)
    setUWBeginDate(underwritingService.getScenarioBeginDate(scenarioid))
    setUWTerm(underwritingService.getScenarioTerm(scenarioid))
  }

  const updateBeginDate = (beginDate: string) => {
    const validBeginDate = underwritingService.validateBeginDate(beginDate, property!, uwTerm)
    const validTerm =  underwritingService.validateTerm(uwTerm, property!, uwValScenario, validBeginDate)
    setUWBeginDate(validBeginDate)
    setUWTerm(validTerm)
  }

  const updateTerm = (term: number) => {
    const validTerm =  underwritingService.validateTerm(term, property!, uwValScenario, uwBeginDate)
    const validBeginDate = underwritingService.validateBeginDate(uwBeginDate, property!, validTerm)
    setUWTerm(validTerm)
    setUWBeginDate(validBeginDate)
  }

  const toggleLinkAsset = () => {
    if(_.get(property, "assetid")) {
      setShowUnlinkConfirmationModal(true)
    } else {
      setLinkMode(!linkMode)
    }
  }

  const attrs = (field) => field_attrs('property')(field)(property, props)
  const dateOptions = DateDetailOptions.map(val => { return { label: val, value: val}})
  const annMonthlyOpts = ['Annual', 'Monthly'].map(val => { return { label: val, value: val}})
  const stateProvOpts = stateProvinceOptions.map(val => { return { label: val, value: val}})

  return (
    <PropertySettings_s id="property-settings-container">
      {showLeaseTypesModal &&
        <Modal show={showLeaseTypesModal} title="Lease Types" toggleShow={toggleLeaseTypesModal} width={400}>
          <LeaseTypes data={_.get(property, "leasetypedetail")} propertyid={_.get(property, "propertyid")} hide={() => toggleLeaseTypesModal(false)} persist={persist} />
        </Modal>
      }
      {showUnlinkConfirmationModal &&
        <ConfirmationModal
          show={showUnlinkConfirmationModal}
          title={'Unlink Asset'}
          confirmText={'OK'}
          message={"Are you sure you want to Unlink this property from its asset? If you proceed this property can't be calculated in *AssetManagement subfolders."}
          confirmFunc={() => unlinkAsset()}
          cancelFunc={() => setShowUnlinkConfirmationModal(false)}
          toggleShow={() => setShowUnlinkConfirmationModal(false)}
          confirmOnly={false}
          closeOnly={false}/>
      }
      {showUnderwritingPopover &&
        <Popover
          position="top left"
          show={showUnderwritingPopover}
          target="propertyUnderwritingIcon"
          title={"Property Underwriting Workbook"}
          left={0}
          top={76}
          toggleShow={() => setShowUnderwritingPopover(!showUnderwritingPopover)}
          width={284}>
          <div className="propertyUnderwriting">
            <div className="column">
              <label htmlFor="val-scenario">Valuation Scenario</label>
              <DropdownInput
                field="val-scenario"
                className="automated"
                options={uwValScenarioOptions}
                value={uwValScenario}
                persist={(_field, val) => updateValuationScenario(val)}/>
            </div>
            <div className="row">
              <div className="column">
                <label htmlFor="uw-begin-date">Begin Date</label>
                <DateText_s
                  id="uw-begin-date"
                  key={`begin-date-${uwBeginDate}`}
                  className="automated"
                  value={uwBeginDate}
                  persist={val => updateBeginDate(formatDate(val, "YYYY-MM-DD"))}
                  withoutDays/>
              </div>
              <div className="column">
                <label htmlFor="uw-term">Term</label>
                <NumberInput
                  field="uw-term"
                  key={`term-${uwTerm}`}
                  className="automated"
                  value={uwTerm}
                  persist={(_field, val) => updateTerm(val)}
                  readPrecision={0}
                  writePrecision={0}/>
              </div>
            </div>
            <PopoverButtons_s>
              <Button_s className="automated" onClick={() => setShowUnderwritingPopover(false)}>Cancel</Button_s>
              <Button_s className="automated" onClick={() => {
                underwritingService.quickReport(null, uwBeginDate, uwTerm, uwValScenario)
                setShowUnderwritingPopover(false)
              }} primary>Submit</Button_s>
            </PopoverButtons_s>
          </div>
        </Popover>
      }
      <Toolbar>
        <Icon_s>
          <PropertyUnderwriting id={"propertyUnderwritingIcon"} position="top left" onClick={() => setShowUnderwritingPopover(!showUnderwritingPopover)}/>
        </Icon_s>
      </Toolbar>
      <div className="input-group">
        <div>
          <label htmlFor="codeInput">Code</label>
          <TextInput
            className="automated"
            id="codeInput"
            key={_.get(property, "code")}
            value={_.get(property, "code")}
            persist={(val) => persist("code", val)}
            attrs={() => attrs('code')}/>
        </div>
        <div>
          <label htmlFor="nameInput">Name</label>
          <TextInput
            className="automated"
            id="nameInput"
            key={_.get(property, "code")}
            value={_.get(property, "name")}
            persist={(val) => persist("name", val)}
            attrs={() => attrs('name')}/>
        </div>
        <div>
          <label htmlFor="codeInput">Property Type</label>
          <TextInput
            className="automated"
            id="codeInput"
            key={_.get(property, "code")}
            value={_.get(property, "type")}
            persist={() => {}}
            disabled/>
        </div>
        { (_.get(property, "type") === "Commercial" || _.get(property, "type") === "Mixed-Use") &&
          <div>
            <label>Lease Types</label>
            <Button_s onClick={() => toggleLeaseTypesModal(!showLeaseTypesModal)}>Edit</Button_s>
          </div>
        }
        {local('auth.profile')?.assetmanagement &&
          [
            <div>
              <Button_s id="link-asset-button" onClick={() => toggleLinkAsset()}>{_.get(property, "assetid") ? "Unlink Asset" : linkMode ? "Linking" : "Link Asset"}</Button_s>
            </div>,
            <div>
              <label htmlFor="assetCode">Asset Code</label>
              <DropdownInput
                field="assetid"
                disabled={_.get(property, "assetid") ? true : linkMode ? false : true}
                className="automated"
                options={assetOptions}
                key={_.get(property, "code")}
                value={currentAssetId}
                filter={true}
                filterPlaceholder="Select an asset"
                persist={(field, assetid) => {
                  setCurrentAssetId(assetid)
                  persist("assetid", assetid)
                }}/>
            </div>,
            <div>
              <label htmlFor="chart">Chart</label>
              <TextInput
                className="automated"
                id="chart"
                disabled={true}
                key={_.get(property, "code")}
                value={_.get(currentChart, "code")}
                persist={() => {}}/>
            </div>
          ]
        }
      </div>
      <div className="blue-input-group">
        <div>
          <label htmlFor="address1Input">Address 1</label>
          <TextInput
            className="automated"
            id="address1Input"
            key={_.get(property, "code")}
            value={_.get(property, "assetid") ? _.get(currentAsset, "address") : _.get(property, "address1")}
            disabled={_.get(property, "assetid")}
            persist={(val) => persist("address1", val)}
            attrs={() => attrs('address1')}/>
        </div>
        <div>
          <label htmlFor="address2Input">Address 2</label>
          <TextInput
            className="automated"
            id="address2Input"
            key={_.get(property, "code")}
            value={_.get(property, "assetid") ? "" : _.get(property, "address2")}
            disabled={_.get(property, "assetid")}
            persist={(val) => persist("address2", val)}
            attrs={() => attrs('address2')}/>
        </div>
        <div>
          <label htmlFor="cityInput">City</label>
          <TextInput
            className="automated"
            id="cityInput"
            key={_.get(property, "code")}
            value={_.get(property, "assetid") ? _.get(currentAsset, "city") : _.get(property, "city")}
            disabled={_.get(property, "assetid")}
            persist={(val) => persist("city", val)}
            attrs={() => attrs('city')}/>
        </div>
        <div>
          <label htmlFor="cityInput">State</label>
          <DropdownInput
            field="state"
            className="automated"
            options={stateProvOpts}
            key={_.get(property, "code")}
            value={_.get(property, "assetid") ? _.get(currentAsset, "state") : _.get(property, "state")}
            disabled={_.get(property, "assetid") ? true : false}
            persist={persist}
            attrs={() => attrs('state')}/>
        </div>
        <div>
          <label htmlFor="postalcodeInput">Zip</label>
          <TextInput
            className="automated"
            id="postalcodeInput"
            key={_.get(property, "code")}
            value={_.get(property, "assetid") ? _.get(currentAsset, "postalcode") : _.get(property, "postalcode")}
            disabled={_.get(property, "assetid")}
            persist={(val) => persist("postalcode", val)}
            attrs={() => attrs('postalcode')}/>
        </div>
      </div>
      <div className="input-group">
        <div>
          <label htmlFor="analysisBegin">Analysis Begin</label>
          <DateText_s
            id="analysisBegin"
            className="automated"
            key={_.get(property, "code")}
            value={_.get(property, "analysisbegindate")}
            persist={val => persist('analysisbegindate', formatDate(val, "YYYY-MM-DD"))}
            withoutDays
            attrs={() => attrs('analysisbegindate')}/>
        </div>
        <div>
          <label htmlFor="term">Term</label>
          <NumberInput
            field="term"
            className="automated"
            key={_.get(property, "code")}
            value={_.get(property, "term")}
            persist={persist}
            readPrecision={0}
            writePrecision={0}
            attrs={() => attrs('term')}/>
        </div>
        <div>
          <label htmlFor="reportbegindate">Report Begin</label>
          <DateText_s
            id="reportBegin"
            className="automated"
            key={_.get(property, "code")}
            value={_.get(property, "reportbegindate")}
            persist={val => persist('reportbegindate', formatDate(val, "YYYY-MM-DD"))}
            withoutDays
            attrs={() => attrs('reportbegindate')}/>
        </div>
        { _.get(property, "type") === "Hospitality" &&
          <div>
            <label htmlFor="adrInflation">Default ADR Inflation</label>
            <DropdownInput
              className="automated"
              field="defaultshospitality.adrInflation"
              options={inflationOptions}
              key={_.get(property, "code")}
              value={_.get(property, "defaultshospitality.adrInflation.inflationNamedItemId")}
              persist={persist}
              attrs={() => attrs('defaultshospitality.adrInflation')}/>
          </div>
        }
      </div>
      <div className="blue-input-group">
        <div>
          <label htmlFor="marketEffectiveMonth">Market Effective Month</label>
          <DropdownInput
            className="automated"
            field="datedetail.marketEffectiveMonth"
            options={dateOptions}
            key={_.get(property, "code")}
            value={_.get(property, "datedetail.marketEffectiveMonth")}
            persist={persist}
            attrs={() => attrs('datedetail.marketEffectiveMonth')}/>
        </div>
        { (_.get(property, "type") === "Commercial" || _.get(property, "type") === "Mixed-Use") &&
        <>
          <div>
            <label htmlFor="recoveryEffectiveMonth">Recovery Effective Month</label>
            <DropdownInput
              className="automated"
              field="datedetail.recoveryEffectiveMonth"
              options={dateOptions}
              key={_.get(property, "code")}
              value={_.get(property, "datedetail.recoveryEffectiveMonth")}
              persist={persist}
              attrs={() => attrs('datedetail.recoveryEffectiveMonth')}/>
          </div>
          <div>
            <label htmlFor="vacancyCreditLossEffectiveMonth">Loss Factor Effective Month</label>
            <DropdownInput
              className="automated"
              field="datedetail.vacancyCreditLossEffectiveMonth"
              options={dateOptions}
              key={_.get(property, "code")}
              value={_.get(property, "datedetail.vacancyCreditLossEffectiveMonth")}
              persist={persist}
              attrs={() => attrs('datedetail.vacancyCreditLossEffectiveMonth')}/>
          </div>
          <div>
            <label htmlFor="lossFactorFrequency">Loss Factor Calculation</label>
            <DropdownInput
              className="automated"
              field="datedetail.lossFactorFrequency"
              options={annMonthlyOpts}
              key={_.get(property, "code")}
              value={_.get(property, "datedetail.lossFactorFrequency")}
              persist={persist}
              attrs={() => attrs('datedetail.lossFactorFrequency')}/>
          </div>
          <div>
            <label htmlFor="recoveryGrossupFrequency">Recovery Gross-up Frequency</label>
            <DropdownInput
              className="automated"
              field="datedetail.recoveryGrossupFrequency"
              options={annMonthlyOpts}
              key={_.get(property, "code")}
              value={_.get(property, "datedetail.recoveryGrossupFrequency")}
              persist={persist}
              attrs={() => attrs('datedetail.recoveryGrossupFrequency')}/>
          </div>
        </>
        }
        <FormAutomation automationContainerId="property-settings-container"/>
      </div>
      <div className="input-group">
        <div>
          <label htmlFor="notesInput">Notes</label>
          <textarea
            id="notesInput"
            rows={10} cols={100}
            className="automated"
            key={_.get(property, "code")}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            onBlur={(e) => persist('notedetail.notes', notes || '')}
          />
        </div>
      </div>
    </PropertySettings_s>
  )
}

export default PropertySettings
